import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { buildCustomerCreatePayload } from "../lib/customerCreate";
import { buildCustomerEditPayload } from "@/lib/customerEdit";
import { fetchCustomerArchivePage } from "../lib/customerArchive";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const sourceLabels = {
  manual: "Inserito manualmente",
  quick_booking: "Creato da appuntamento rapido",
  online_booking: "Creato da prenotazione online",
};

const extractErrorMessage = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (typeof detail?.message === "string" && detail.message.trim()) {
    return detail.message.trim();
  }
  return fallbackMessage;
};

const getSourceLabel = (source) => {
  if (!source) {
    return "Origine non disponibile";
  }
  return sourceLabels[source] || `Origine: ${source}`;
};

const getSourceFilterValue = (source) => {
  if (source === "manual" || source === "quick_booking" || source === "online_booking") {
    return source;
  }
  return "other";
};

const getNoShowLabel = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "Non disponibile";
  }
  return String(numericValue);
};

const CUSTOMER_SEARCH_MIN_LENGTH = 2;
const CUSTOMER_SEARCH_LIMIT = 20;

export function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState("");
  const [browseHasMore, setBrowseHasMore] = useState(false);
  const [trashingCustomerId, setTrashingCustomerId] = useState("");
  const [expandedCustomerId, setExpandedCustomerId] = useState("");
  const [editingCustomerId, setEditingCustomerId] = useState("");
  const [savingCustomerId, setSavingCustomerId] = useState("");
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_staff_id: "",
  });
  const searchRequestId = useRef(0);
  const browseRequestId = useRef(0);

  const loadData = async () => {
    setLoading(true);
    try {
      const staffRes = await api.get("/staff");
      setStaff(staffRes.data);
    } catch (error) {
      toast.error("Non riesco a caricare i clienti. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const searchCustomers = useCallback(async (term) => {
    const normalizedTerm = term.trim();
    const requestId = searchRequestId.current + 1;
    searchRequestId.current = requestId;
    if (normalizedTerm.length < CUSTOMER_SEARCH_MIN_LENGTH) {
      setCustomers([]);
      setSearchError("");
      return;
    }
    setSearchLoading(true);
    setSearchError("");
    try {
      const response = await api.get("/customers", {
        params: { q: normalizedTerm, limit: CUSTOMER_SEARCH_LIMIT },
      });
      if (searchRequestId.current === requestId) {
        setCustomers(response.data);
      }
    } catch (error) {
      if (searchRequestId.current === requestId) {
        setCustomers([]);
        setSearchError("Non riesco a cercare i clienti. Riprova.");
      }
    } finally {
      if (searchRequestId.current === requestId) {
        setSearchLoading(false);
      }
    }
  }, []);

  const loadBrowsePage = useCallback(async (offset, replace = false) => {
    const requestId = browseRequestId.current + 1;
    browseRequestId.current = requestId;
    setBrowseLoading(true);
    setBrowseError("");
    try {
      const page = await fetchCustomerArchivePage(offset);
      if (browseRequestId.current === requestId) {
        setCustomers((current) => replace ? page.items : [...current, ...page.items]);
        setBrowseHasMore(page.hasMore);
      }
    } catch (error) {
      if (browseRequestId.current === requestId) {
        if (replace) setCustomers([]);
        setBrowseError("Non riesco a caricare l’archivio. Riprova.");
      }
    } finally {
      if (browseRequestId.current === requestId) {
        setBrowseLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (browseOpen) return undefined;
    const normalizedTerm = searchTerm.trim();
    if (normalizedTerm.length < CUSTOMER_SEARCH_MIN_LENGTH) {
      searchRequestId.current += 1;
      setCustomers([]);
      setSearchError("");
      setSearchLoading(false);
      return undefined;
    }
    const timerId = window.setTimeout(() => searchCustomers(normalizedTerm), 250);
    return () => window.clearTimeout(timerId);
  }, [browseOpen, searchCustomers, searchTerm]);

  const activeStaff = useMemo(() => {
    return staff.filter((member) => member.active !== false);
  }, [staff]);

  const staffMap = useMemo(() => {
    return staff.reduce((acc, member) => {
      acc[member.id] = member;
      return acc;
    }, {});
  }, [staff]);

  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      const matchesSource =
        sourceFilter === "all" || getSourceFilterValue(customer.source) === sourceFilter;
      return matchesSource;
    });
  }, [customers, sourceFilter]);

  const normalizedSearchTerm = searchTerm.trim();
  const hasSearchQuery = normalizedSearchTerm.length >= CUSTOMER_SEARCH_MIN_LENGTH;
  const isArchiveVisible = browseOpen || hasSearchQuery;
  const hasActiveFilters = browseOpen || normalizedSearchTerm !== "" || sourceFilter !== "all";

  const handleChange = (event) => {
    setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const clearFilters = () => {
    browseRequestId.current += 1;
    setBrowseOpen(false);
    setBrowseError("");
    setBrowseHasMore(false);
    setCustomers([]);
    setSearchTerm("");
    setSourceFilter("all");
  };

  const openArchive = () => {
    searchRequestId.current += 1;
    setSearchTerm("");
    setSearchError("");
    setBrowseOpen(true);
    loadBrowsePage(0, true);
  };

  const handleSearchChange = (event) => {
    browseRequestId.current += 1;
    setBrowseOpen(false);
    setBrowseError("");
    setBrowseHasMore(false);
    setSearchTerm(event.target.value);
  };

  const submitCustomer = async (event) => {
    event.preventDefault();
    let payload;
    try {
      payload = buildCustomerCreatePayload(form);
    } catch (error) {
      toast.error(error.message);
      return;
    }

    try {
      await api.post("/customers", payload);
      toast.success("Cliente aggiunto.");
      setForm({
        name: "",
        email: "",
        phone: "",
        preferred_staff_id: "",
      });
      if (browseOpen) {
        await loadBrowsePage(0, true);
      } else {
        await searchCustomers(searchTerm);
      }
    } catch (error) {
      toast.error("Impossibile creare il cliente.");
    }
  };

  const moveCustomerToTrash = async (customer) => {
    const confirmed = window.confirm(
      "Spostare questo cliente nel cestino? Potrai ripristinarlo dalla pagina Cestino."
    );
    if (!confirmed) {
      return;
    }

    setTrashingCustomerId(customer.id);
    try {
      await api.post(`/customers/${customer.id}/trash`);
      toast.success("Cliente spostato nel cestino.");
      setCustomers((current) => current.filter((item) => item.id !== customer.id));
    } catch (error) {
      toast.error(extractErrorMessage(error, "Impossibile spostare il cliente nel cestino."));
    } finally {
      setTrashingCustomerId("");
    }
  };

  const startEditingCustomer = (customer) => {
    setEditingCustomerId(customer.id);
    setEditForm({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
    });
  };

  const cancelEditingCustomer = () => {
    setEditingCustomerId("");
    setEditForm({ name: "", email: "", phone: "" });
  };

  const saveCustomer = async (event, customer) => {
    event.preventDefault();
    let payload;
    try {
      payload = buildCustomerEditPayload(editForm);
    } catch (error) {
      toast.error(error.message);
      return;
    }

    setSavingCustomerId(customer.id);
    try {
      const response = await api.patch(`/customers/${customer.id}`, payload);
      setCustomers((current) => current.map((item) => (
        item.id === customer.id ? response.data : item
      )));
      cancelEditingCustomer();
      toast.success("Cliente aggiornato.");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Impossibile aggiornare il cliente."));
    } finally {
      setSavingCustomerId("");
    }
  };

  const getPreferredStaffLabel = (customer) => {
    if (!customer.preferred_staff_id) {
      return "Staff preferito non impostato";
    }
    const preferredStaff = staffMap[customer.preferred_staff_id];
    if (preferredStaff?.name) {
      return preferredStaff.name;
    }
    return `Staff ID: ${customer.preferred_staff_id}`;
  };

  if (loading) {
    return <div data-testid="customers-loading">Caricamento clienti...</div>;
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      <PageHeader
        title="Clienti"
        subtitle="Cerca, aggiorna o aggiungi i clienti della tua attività."
        titleTestId="customers-title"
      />
      <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="customers-form-card">
        <h2 className="text-lg font-semibold section-title" data-testid="customers-form-title">
          Nuovo cliente
        </h2>
        <p className="mt-1 text-sm text-slate-500">Salva un cliente nell’archivio della tua attività.</p>
        <form className="mt-4 grid md:grid-cols-2 gap-4" onSubmit={submitCustomer} data-testid="customers-form">
          <input
            name="name"
            aria-label="Nome cliente"
            value={form.name}
            onChange={handleChange}
            placeholder="Nome cliente"
            required
            maxLength={120}
            className="border border-slate-200 rounded-sm h-10 px-3"
            data-testid="customer-name-input"
          />
          <input
            name="email"
            aria-label="Email cliente"
            value={form.email}
            onChange={handleChange}
            placeholder="nome@email.it"
            className="border border-slate-200 rounded-sm h-10 px-3"
            data-testid="customer-email-input"
          />
          <input
            name="phone"
            aria-label="Telefono cliente"
            value={form.phone}
            onChange={handleChange}
            placeholder="Telefono"
            className="border border-slate-200 rounded-sm h-10 px-3"
            data-testid="customer-phone-input"
          />
          <Select
            value={form.preferred_staff_id || "any"}
            onValueChange={(value) =>
              setForm((prev) => ({
                ...prev,
                preferred_staff_id: value === "any" ? "" : value,
              }))
            }
          >
            <SelectTrigger data-testid="customer-preferred-staff-select">
              <SelectValue placeholder="Staff preferito (opzionale)" />
            </SelectTrigger>
            <SelectContent data-testid="customer-preferred-staff-options">
              <SelectItem value="any" data-testid="customer-staff-option-any">
                Qualsiasi staff
              </SelectItem>
              {activeStaff.map((member) => (
                <SelectItem
                  key={member.id}
                  value={member.id}
                  data-testid={`customer-staff-option-${member.id}`}
                >
                  {member.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="submit"
            className="bg-slate-900 text-white rounded-sm h-10 px-4 font-medium transition-transform active:scale-95"
            data-testid="customer-submit-button"
          >
            Salva cliente
          </button>
        </form>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="customers-list-card">
        <h2 className="text-lg font-semibold section-title" data-testid="customers-list-title">
          Archivio clienti
        </h2>
        <p className="mt-1 text-sm text-slate-500">Cerca un cliente per nome, telefono o email.</p>
        <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_240px_auto_auto]" data-testid="customers-filters">
          <input
            type="search"
            aria-label="Cerca clienti"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Cerca per nome, email o telefono..."
            className="border border-slate-200 rounded-sm h-10 px-3"
            data-testid="customers-search-input"
          />
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger data-testid="customers-source-filter">
              <SelectValue placeholder="Origine" />
            </SelectTrigger>
            <SelectContent data-testid="customers-source-filter-options">
              <SelectItem value="all" data-testid="customers-source-filter-all">
                Tutte le origini
              </SelectItem>
              <SelectItem value="manual" data-testid="customers-source-filter-manual">
                Inseriti manualmente
              </SelectItem>
              <SelectItem value="quick_booking" data-testid="customers-source-filter-quick">
                Creati da appuntamento rapido
              </SelectItem>
              <SelectItem value="online_booking" data-testid="customers-source-filter-online">
                Creati da prenotazione online
              </SelectItem>
              <SelectItem value="other" data-testid="customers-source-filter-other">
                Origine non disponibile / Altro
              </SelectItem>
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={openArchive}
            aria-pressed={browseOpen}
            className={`rounded-sm border px-3 h-10 text-sm font-medium ${
              browseOpen
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-300 bg-white text-slate-900"
            }`}
            data-testid="customers-browse-toggle"
          >
            Sfoglia archivio
          </button>
          <button
            type="button"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="border border-slate-200 text-slate-900 rounded-sm px-3 h-10 text-sm font-medium disabled:opacity-50"
            data-testid="customers-clear-filters"
          >
            Pulisci filtri
          </button>
        </div>
        {!isArchiveVisible && (
          <div
            className="mt-4 rounded-sm border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500"
            data-testid="customers-search-prompt"
          >
            Inserisci almeno 2 caratteri per cercare oppure scegli “Sfoglia archivio”.
          </div>
        )}
        {hasSearchQuery && searchLoading && <p className="mt-4 text-sm text-slate-500" data-testid="customers-search-loading">Ricerca in corso...</p>}
        {hasSearchQuery && searchError && <p className="mt-4 text-sm text-red-600" data-testid="customers-search-error">{searchError}</p>}
        {browseOpen && browseLoading && customers.length === 0 && <p className="mt-4 text-sm text-slate-500" data-testid="customers-browse-loading">Caricamento archivio...</p>}
        {browseOpen && browseError && <p className="mt-4 text-sm text-red-600" data-testid="customers-browse-error">{browseError}</p>}
        {hasSearchQuery && !searchLoading && !searchError && (
          <p className="mt-3 text-xs text-slate-500" data-testid="customers-results-count">
            {filteredCustomers.length} {filteredCustomers.length === 1 ? "risultato" : "risultati"}
          </p>
        )}
        {hasSearchQuery && !searchLoading && !searchError && filteredCustomers.length === 0 && (
          <div
            className="mt-4 rounded-sm border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500"
            data-testid="customers-filter-empty"
          >
            <p>Nessun cliente trovato.</p>
            <p>Prova a modificare la ricerca o i filtri.</p>
          </div>
        )}
        {browseOpen && !browseLoading && !browseError && (
          <p className="mt-3 text-xs text-slate-500" data-testid="customers-browse-count">
            {customers.length} {customers.length === 1 ? "cliente mostrato" : "clienti mostrati"}
            {browseHasMore && (
              <span className="ml-2 font-medium" data-testid="customers-browse-more-available">
                Altri clienti disponibili
              </span>
            )}
          </p>
        )}
        {isArchiveVisible && !searchLoading && !searchError && !(browseOpen && customers.length === 0 && browseLoading) && <div className="mt-4 max-h-[32rem] space-y-3 overflow-y-auto overscroll-contain" data-testid="customers-list">
          {filteredCustomers.map((customer) => {
            const isTrashing = trashingCustomerId === customer.id;
            const isEditing = editingCustomerId === customer.id;
            const isSaving = savingCustomerId === customer.id;
            const isExpanded = expandedCustomerId === customer.id;
            const preferredStaffLabel = getPreferredStaffLabel(customer);

            return (
              <div
                key={customer.id}
                className="border border-slate-200 rounded-sm p-4"
                data-testid={`customer-${customer.id}`}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-3">
                      <div className="space-y-1">
                        <p className="text-sm font-medium" data-testid={`customer-name-${customer.id}`}>
                          {customer.name}
                        </p>
                        <p
                          className="break-all text-xs text-slate-500"
                          data-testid={`customer-email-${customer.id}`}
                        >
                          {customer.email || "Nessuna email"}
                        </p>
                        <p className="text-xs text-slate-500" data-testid={`customer-phone-${customer.id}`}>
                          {customer.phone || "Nessun telefono"}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <span
                          className="rounded-sm border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600"
                          data-testid={`customer-source-${customer.id}`}
                        >
                          {getSourceLabel(customer.source)}
                        </span>
                        <span
                          className="rounded-sm border border-slate-200 px-2 py-1 text-xs text-slate-600"
                          data-testid={`customer-preferred-staff-${customer.id}`}
                        >
                          {preferredStaffLabel}
                        </span>
                        <span
                          className="rounded-sm border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700"
                          data-testid={`customer-noshow-${customer.id}`}
                        >
                          No-show {getNoShowLabel(customer.noshow_count)}
                        </span>
                      </div>
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => startEditingCustomer(customer)}
                        disabled={isSaving}
                        className="border border-slate-200 text-slate-900 rounded-sm px-3 h-9 text-sm font-medium disabled:opacity-60"
                        data-testid={`customer-edit-${customer.id}`}
                      >
                        Modifica
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedCustomerId((prev) => (prev === customer.id ? "" : customer.id))
                        }
                        className="border border-slate-200 text-slate-900 rounded-sm px-3 h-9 text-sm font-medium"
                        data-testid={`customer-details-${customer.id}`}
                      >
                        {isExpanded ? "Nascondi dettagli" : "Dettagli"}
                      </button>
                      <button
                        type="button"
                        onClick={() => moveCustomerToTrash(customer)}
                        disabled={isTrashing}
                        className="destructive-action shrink-0 rounded-sm px-3 h-9 text-sm font-medium disabled:opacity-60"
                        data-testid={`customer-trash-${customer.id}`}
                      >
                        {isTrashing ? "Spostamento..." : "Sposta nel cestino"}
                      </button>
                    </div>
                  </div>

                  {isEditing && (
                    <form
                      className="grid gap-3 border-t border-slate-200 pt-4 md:grid-cols-3"
                      onSubmit={(event) => saveCustomer(event, customer)}
                      data-testid={`customer-edit-form-${customer.id}`}
                    >
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(event) => setEditForm((current) => ({ ...current, name: event.target.value }))}
                        placeholder="Nome cliente"
                        required
                        maxLength={120}
                        className="border border-slate-200 rounded-sm h-10 px-3"
                        data-testid={`customer-edit-name-${customer.id}`}
                      />
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(event) => setEditForm((current) => ({ ...current, email: event.target.value }))}
                        placeholder="nome@email.it"
                        className="border border-slate-200 rounded-sm h-10 px-3"
                        data-testid={`customer-edit-email-${customer.id}`}
                      />
                      <input
                        type="tel"
                        value={editForm.phone}
                        onChange={(event) => setEditForm((current) => ({ ...current, phone: event.target.value }))}
                        placeholder="Telefono"
                        maxLength={40}
                        className="border border-slate-200 rounded-sm h-10 px-3"
                        data-testid={`customer-edit-phone-${customer.id}`}
                      />
                      <div className="flex gap-2 md:col-span-3">
                        <button
                          type="submit"
                          disabled={isSaving}
                          className="bg-slate-900 text-white rounded-sm h-9 px-3 text-sm font-medium disabled:opacity-60"
                          data-testid={`customer-edit-save-${customer.id}`}
                        >
                          {isSaving ? "Salvataggio..." : "Salva modifiche"}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditingCustomer}
                          disabled={isSaving}
                          className="border border-slate-200 text-slate-700 rounded-sm h-9 px-3 text-sm font-medium disabled:opacity-60"
                          data-testid={`customer-edit-cancel-${customer.id}`}
                        >
                          Annulla
                        </button>
                      </div>
                    </form>
                  )}

                  {isExpanded && (
                    <div
                      className="grid gap-4 border-t border-slate-200 pt-4 md:grid-cols-3"
                      data-testid={`customer-details-panel-${customer.id}`}
                    >
                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                          Contatti
                        </p>
                        <p className="break-all text-sm text-slate-700">
                          Email: {customer.email || "Nessuna email"}
                        </p>
                        <p className="break-all text-sm text-slate-700">
                          Telefono: {customer.phone || "Nessun telefono"}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                          Preferenze
                        </p>
                        <p className="text-sm text-slate-700">
                          Staff preferito: {preferredStaffLabel}
                        </p>
                        <p className="text-sm text-slate-700">
                          No-show: {getNoShowLabel(customer.noshow_count)}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                          Origine
                        </p>
                        <p className="text-sm text-slate-700">
                          Source: {getSourceLabel(customer.source)}
                        </p>
                        <p className="break-all text-sm text-slate-700">
                          ID cliente: {customer.id || "Non disponibile"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {browseOpen && (browseHasMore || browseLoading) && (
            <button
              type="button"
              onClick={() => loadBrowsePage(customers.length)}
              disabled={browseLoading}
              className="min-h-11 w-full rounded-sm border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 disabled:opacity-60"
              data-testid="customers-browse-load-more"
            >
              {browseLoading ? "Caricamento..." : "Carica altri clienti"}
            </button>
          )}
        </div>}
      </div>
    </div>
  );
}
